import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  Tooltip,
  useMediaQuery,
  useTheme,
  MenuItem,
  Select,
  TextField,
  Pagination
} from "@mui/material";
import {
  Visibility,
  Edit,
  Delete,
  Circle,
  CheckCircle,
  Warning,
  Block,
  MoreVert,
  Add
} from "@mui/icons-material";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";

const STAGE_CONFIG = {
  screening: { icon: <Circle fontSize="small" />, color: "default", label: "Screening" },
  interview: { icon: <Circle fontSize="small" />, color: "primary", label: "Interview" },
  offer: { icon: <Warning fontSize="small" />, color: "warning", label: "Offer" },
  hired: { icon: <CheckCircle fontSize="small" />, color: "success", label: "Hired" },
  rejected: { icon: <Block fontSize="small" />, color: "error", label: "Rejected" }
};

const ITEMS_PER_PAGE = 5;

export default function CandidateListPage() {
  const [allCandidates, setAllCandidates] = useState([]);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [displayedCandidates, setDisplayedCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [stageUpdating, setStageUpdating] = useState(null);
  const [search, setSearch] = useState("");
  const [stageFilter, setStageFilter] = useState("all");
  const [page, setPage] = useState(1);

  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    loadCandidates();
  }, []);

  useEffect(() => {
    const lowerSearch = search.toLowerCase();
    const filtered = allCandidates.filter((c) =>
      `${c.full_name} ${c.email}`.toLowerCase().includes(lowerSearch) &&
      (stageFilter === "all" || c.recruitment_stage === stageFilter)
    );
    setFilteredCandidates(filtered);
    setPage(1);
  }, [search, stageFilter, allCandidates]);

  useEffect(() => {
    const start = (page - 1) * ITEMS_PER_PAGE;
    const end = start + ITEMS_PER_PAGE;
    setDisplayedCandidates(filteredCandidates.slice(start, end));
  }, [filteredCandidates, page]);

  const loadCandidates = async () => {
    try {
      const { data } = await api.get("/recruiter/candidates");
      setAllCandidates(data);
    } catch (error) {
      console.error("Error loading candidates:", error);
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = (candidate) => {
    setSelectedCandidate(candidate);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/recruiter/candidates/${selectedCandidate.id}`);
      setAllCandidates((prev) => prev.filter((c) => c.id !== selectedCandidate.id));
      setConfirmOpen(false);
      setSelectedCandidate(null);
    } catch (err) {
      console.error("Failed to delete candidate", err);
      alert("❌ Failed to delete candidate.");
    }
  };

  const handleStageChange = async (candidateId, newStage) => {
    setStageUpdating(candidateId);
    try {
      await api.put(`/recruiter/candidates/${candidateId}`, {
        recruitment_stage: newStage,
      });
      setAllCandidates((prev) =>
        prev.map((c) =>
          c.id === candidateId ? { ...c, recruitment_stage: newStage } : c
        )
      );
    } catch (err) {
      console.error("Failed to update stage", err);
      alert("❌ Failed to update recruitment stage.");
    } finally {
      setStageUpdating(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "SAR",
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <Box p={isMobile ? 1 : 3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5">Candidate Pipeline</Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => navigate("/recruiter/candidates/create")}
        >
          Create New Candidate
        </Button>
      </Box>

      <Box display="flex" flexDirection={isMobile ? "column" : "row"} gap={2} mb={2}>
        <TextField
          label="Search candidates"
          variant="outlined"
          size="small"
          fullWidth={isMobile}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={stageFilter}
          size="small"
          onChange={(e) => setStageFilter(e.target.value)}
          fullWidth={isMobile}
        >
          <MenuItem value="all">All Stages</MenuItem>
          {Object.keys(STAGE_CONFIG).map((stage) => (
            <MenuItem key={stage} value={stage}>
              {STAGE_CONFIG[stage].label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Paper sx={{ overflowX: "auto", boxShadow: theme.shadows[2] }}>
            <Table size={isMobile ? "small" : "medium"}>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.grey[100] }}>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Contact</TableCell>
                  {!isMobile && <TableCell align="right">Salary</TableCell>}
                  <TableCell>Stage</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedCandidates.map((candidate) => (
                  <TableRow
                    key={candidate.id}
                    hover
                    sx={{
                      '&:last-child td': { border: 0 },
                      opacity: stageUpdating === candidate.id ? 0.7 : 1
                    }}
                  >
                    <TableCell>
                      <Typography fontWeight="500">{candidate.full_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {candidate.email}
                      </Typography>
                      <Typography variant="body2">{candidate.phone}</Typography>
                    </TableCell>
                    {!isMobile && (
                      <TableCell align="right">
                        {formatCurrency(candidate.expected_salary)}
                      </TableCell>
                    )}
                    <TableCell>
                      {stageUpdating === candidate.id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <Select
                          size="small"
                          value={candidate.recruitment_stage}
                          onChange={(e) =>
                            handleStageChange(candidate.id, e.target.value)
                          }
                          displayEmpty
                        >
                          {Object.keys(STAGE_CONFIG).map((stage) => (
                            <MenuItem key={stage} value={stage}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <Circle fontSize="small" color={STAGE_CONFIG[stage].color} />
                                {STAGE_CONFIG[stage].label}
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center">
                        {isMobile ? (
                          <Tooltip title="More actions">
                            <IconButton
                              onClick={() =>
                                navigate(`/recruiter/candidates/${candidate.id}`)
                              }
                            >
                              <MoreVert />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <>
                            <Tooltip title="View details">
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  navigate(`/recruiter/candidates/${candidate.id}`)
                                }
                              >
                                <Visibility />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                color="warning"
                                onClick={() =>
                                  navigate(`/recruiter/candidates/${candidate.id}/edit`)
                                }
                              >
                                <Edit />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                color="error"
                                onClick={() => confirmDelete(candidate)}
                              >
                                <Delete />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
                {displayedCandidates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No candidates found.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={Math.ceil(filteredCandidates.length / ITEMS_PER_PAGE)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              size="medium"
            />
          </Box>
        </>
      )}

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>
          Delete {selectedCandidate?.full_name}?
        </DialogTitle>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            startIcon={<Delete />}
          >
            Confirm Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
